/**
 * QuoteShareModal — share sheet for the daily Pivot quote.
 * Renders the 1080x1080 PNG card with Share / Save / Copy actions.
 * Supports an optional `milestoneStreak` prop that adds a celebration banner.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Share2, Download, Copy, X, Check, Flame, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CARD_URL = `${API}/share/quote-card`;

const QuoteShareModal = ({ open, onClose, quote, milestoneStreak }) => {
  const [copied, setCopied] = useState(false);
  const [sharesToday, setSharesToday] = useState(null);
  const dayKey = new Date().toISOString().slice(0, 10);
  const cardSrc = `${CARD_URL}?d=${dayKey}`;
  const isMilestone = !!milestoneStreak;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/share/quote-card/today`);
        setSharesToday(res.data?.shares_today ?? 0);
      } catch {
        // ignore
      }
    })();
  }, [open]);

  const recordShare = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/share/quote-card/share`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSharesToday(res.data?.shares_today ?? null);
    } catch {
      // non-fatal
    }
  };

  const handleDownload = async () => {
    try {
      const res = await fetch(cardSrc);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pivot-quote-${dayKey}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Card downloaded");
      recordShare();
    } catch {
      toast.error("Download failed");
    }
  };

  const handleNativeShare = async () => {
    try {
      const res = await fetch(cardSrc);
      const blob = await res.blob();
      const file = new File([blob], `pivot-quote-${dayKey}.png`, { type: "image/png" });
      const shareText = isMilestone
        ? `🔥 ${milestoneStreak}-day Pivot Performance streak. The grind doesn't break.`
        : quote
        ? `"${quote}" — Pivot`
        : "Today's grind quote from Pivot Performance";
      const shareData = {
        title: "Pivot Performance",
        text: shareText,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        recordShare();
        return;
      }
      await navigator.share({
        title: "Pivot Performance",
        text: shareText,
        url: window.location.origin,
      });
      recordShare();
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast.error("Sharing not supported on this device — try Download instead");
      }
    }
  };

  const handleCopyText = async () => {
    try {
      const text = isMilestone
        ? `🔥 ${milestoneStreak}-day Pivot Performance streak.\n${window.location.origin}`
        : quote
        ? `"${quote}" — Pivot\n${window.location.origin}`
        : window.location.origin;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Quote copied");
      recordShare();
    } catch {
      toast.error("Copy failed");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="quote-share-modal"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0A0A0A] border border-white/[0.06] p-5 max-w-md w-full max-h-[92vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-label={isMilestone ? "Streak milestone share" : "Share daily quote"}
      >
        {/* Milestone celebration banner */}
        {isMilestone && (
          <div
            className="mb-4 p-4 bg-gradient-to-r from-orange-500/20 to-transparent border-l-2 border-orange-500"
            data-testid="milestone-banner"
          >
            <div className="flex items-center gap-3">
              <Flame className="w-7 h-7 text-orange-500" />
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-orange-400">
                  Milestone Unlocked
                </p>
                <p className="text-xl font-heading text-white uppercase tracking-tight">
                  {milestoneStreak}-Day Streak 🔥
                </p>
              </div>
            </div>
            <p className="text-xs text-white/60 mt-2">
              Share this win — tell the world you don't break.
            </p>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] font-mono text-[#FF4500] uppercase tracking-[0.3em]">
              Share Card
            </p>
            <h3 className="text-xl font-heading text-white uppercase">
              {isMilestone ? "Streak Win" : "Quote of the Day"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white"
            data-testid="quote-share-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Card preview */}
        <div className="aspect-square w-full bg-black mb-3 overflow-hidden">
          <img
            src={cardSrc}
            alt="Pivot Performance Share Card"
            className="w-full h-full object-contain"
            data-testid="quote-card-preview"
          />
        </div>

        {/* Social proof — shares today counter */}
        {sharesToday !== null && sharesToday > 0 && (
          <div
            className="flex items-center justify-center gap-2 mb-4 text-[11px] font-mono uppercase tracking-[0.2em] text-white/40"
            data-testid="shares-today-counter"
          >
            <Users className="w-3.5 h-3.5 text-[#FF4500]" />
            <span>
              Shared <span className="text-white font-bold">{sharesToday}</span>{" "}
              {sharesToday === 1 ? "time" : "times"} today
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-3 gap-2">
          <Button
            onClick={handleNativeShare}
            className="bg-[#FF4500] hover:bg-[#FF6326] text-white font-heading uppercase tracking-wider text-xs rounded-none"
            data-testid="quote-share-native-btn"
          >
            <Share2 className="w-4 h-4 mr-1" /> Share
          </Button>
          <Button
            onClick={handleDownload}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/5 font-heading uppercase tracking-wider text-xs rounded-none"
            data-testid="quote-share-download-btn"
          >
            <Download className="w-4 h-4 mr-1" /> Save
          </Button>
          <Button
            onClick={handleCopyText}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/5 font-heading uppercase tracking-wider text-xs rounded-none"
            data-testid="quote-share-copy-btn"
          >
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>

        <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] text-center mt-4">
          1080 × 1080 · Perfect for IG Stories
        </p>
      </motion.div>
    </div>
  );
};

export default QuoteShareModal;
